
import { Component, ChangeDetectionStrategy, input, output, effect, inject, signal, computed } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule, DOCUMENT, DatePipe } from '@angular/common'; 
import { Router } from '@angular/router';

import { Prescription, PrescriptionDetail, Customer } from '../../models/index';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { GeminiService } from '../../services/gemini.service';
import { PrescriptionService } from '../../services/prescription.service'; 
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-smart-prescription-modal',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, TranslatePipe, DatePipe], 
  templateUrl: './smart-prescription-modal.component.html',
  providers: [GeminiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartPrescriptionModalComponent {
  private dataService = inject(DataService);
  private prescriptionService = inject(PrescriptionService); 
  private authService = inject(AuthService);
  private translationService = inject(TranslationService);
  private notificationService = inject(NotificationService);
  private router: Router = inject(Router);
  geminiService = inject(GeminiService);
  private document: Document = inject(DOCUMENT);
  
  isOpen = input.required<boolean>();
  prescription = input<Prescription | null>();
  customer = input<Customer | null>();
  
  closeModal = output<void>();
  prescriptionReviewed = output<void>();

  prescriptionForm: FormGroup;
  
  // State
  aiPrompt = signal('');
  isGenerating = signal(false);
  isSubmitting = signal(false);
  errorMessage = signal<string | null>(null);
  
  // Quota & Warning State
  activePrescriptionWarning = signal<string | null>(null);
  isCheckingQuota = signal(false);

  // Tabs
  activeTab = signal<'current' | 'history'>('current');
  patientHistory = signal<Prescription[]>([]);
  isLoadingHistory = signal(false);
  
  // VIEW STATE: main | rejecting | success
  view = signal<'main' | 'rejecting' | 'success'>('main');
  
  rejectionNotes = signal('');
  successPrescriptionId = signal<string | null>(null);

  // Mode determines if we're reviewing a request or creating a new prescription
  mode = signal<'review' | 'create'>('review');
  activeCustomer = signal<Partial<Customer> | null>(null);

  // Role Check
  isDoctor = computed(() => this.authService.currentUser()?.role === 'doctor' || this.authService.currentUser()?.role === 'admin' || this.authService.currentUser()?.role === 'owner');

  hasVitals = computed(() => {
    const p = this.prescription();
    if (!p) return false;
    return !!(p.temperature_c || p.pulse_rate || p.respiration_rate || p.blood_pressure || p.weight_kg || p.height_cm);
  });

  constructor() {
    const fb: FormBuilder = inject(FormBuilder);
    this.prescriptionForm = fb.group({
        symptoms: ['', Validators.required],
        dosagePerDay: [null, [Validators.required, Validators.min(0.1)]],
        dosageUnit: ['GRAM', Validators.required],
        durationDays: [null, [Validators.required, Validators.min(1)]],
        totalAmount: [{value: null, disabled: true}, Validators.required],
        usageInstructions: ['', Validators.required],
    });

    effect(() => {
        if(this.isOpen()) {
            this.resetState();
            
            const pres = this.prescription();
            const cust = this.customer();

            // Disable form if not a doctor (View Only Mode)
            if (!this.isDoctor()) {
                this.prescriptionForm.disable();
            } else {
                this.prescriptionForm.enable();
                this.prescriptionForm.get('totalAmount')?.disable();
            }

            let targetCustomerId: string | undefined;

            if (pres) { // Review mode
              this.mode.set('review');
              targetCustomerId = pres.customerId;
              this.activeCustomer.set({
                id: pres.customerId,
                fullName: pres.customerName,
                citizenId: pres.customerCitizenId,
                birthDate: pres.customerBirthDate
              });
              
              if (pres.symptoms && pres.symptoms.length > 0) {
                  this.prescriptionForm.patchValue({ symptoms: pres.symptoms.join(', ') });
              }
              if (pres.approvedItems) {
                  this.prescriptionForm.patchValue(pres.approvedItems);
              }
              this.loadHistory(pres.customerId);

            } else if (cust) { // Create mode
              this.mode.set('create');
              targetCustomerId = cust.id;
              this.activeCustomer.set(cust);
              this.loadHistory(cust.id);
            } else {
              this.activeCustomer.set(null);
            }

            // Restore the Quota Check Logic
            if (targetCustomerId) {
                // Add a slight delay to ensure UI renders smoothly before async check
                setTimeout(() => this.checkDuplicatePrescription(targetCustomerId!), 50);
            }
        }
    });

    this.prescriptionForm.get('dosagePerDay')?.valueChanges.subscribe(() => this.calculateTotal());
    this.prescriptionForm.get('durationDays')?.valueChanges.subscribe(() => this.calculateTotal());
  }

  // FIXED: Enhanced Duplicate/Quota Checker
  async checkDuplicatePrescription(customerId: string) {
      this.isCheckingQuota.set(true);
      this.activePrescriptionWarning.set(null); // Clear previous

      try {
          // This service calls the 'get_valid_prescriptions_for_customer' RPC
          const activeQuotas = await this.dataService.getValidPrescriptionsForCustomer(customerId);
          
          if (activeQuotas && activeQuotas.length > 0) {
              const quota = activeQuotas[0];
              let expiryText = 'ไม่ระบุ';

              if (quota.expiresAt) {
                  const d = new Date(quota.expiresAt);
                  // Format date nicely based on locale
                  if (this.translationService.currentLang() === 'th') {
                      const thaiMonthsShort = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
                      expiryText = `${d.getDate()} ${thaiMonthsShort[d.getMonth()]} ${d.getFullYear() + 543}`;
                  } else {
                      const datePipe = new DatePipe('en-US');
                      expiryText = datePipe.transform(d, 'dd MMM yyyy') || 'N/A';
                  }
              }
              
              const displayUnit = quota.dosageUnit === 'GRAM' ? 'กรัม' : 
                                quota.dosageUnit === 'PIECE' ? 'ชิ้น' : 
                                (this.translationService.currentLang() === 'th' ? 'หน่วย' : 'units');

              this.activePrescriptionWarning.set(
                  `⚠️ ผู้ป่วยมีโควต้าคงเหลือ: ${quota.remainingQuota} ${displayUnit} (หมดอายุ: ${expiryText})`
              );
          }
      } catch (e) {
          console.warn("Active quota check failed", e);
          // Don't block the UI, just fail silently or show generic warning if needed
      } finally {
          this.isCheckingQuota.set(false);
      }
  }

  resetState() {
    this.aiPrompt.set('');
    this.prescriptionForm.reset({ dosageUnit: 'GRAM' });
    this.view.set('main');
    this.rejectionNotes.set('');
    this.successPrescriptionId.set(null);
    this.activeTab.set('current');
    this.patientHistory.set([]);
    this.activePrescriptionWarning.set(null);
    this.isCheckingQuota.set(false);
  }

  async loadHistory(customerId: string) {
      this.isLoadingHistory.set(true);
      try {
          const history = await this.prescriptionService.getCustomerHistory(customerId);
          this.patientHistory.set(history);
      } catch (e) {
          console.error("Failed to load patient history", e);
      } finally {
          this.isLoadingHistory.set(false);
      }
  }

  calculateTotal() {
    const dosage = this.prescriptionForm.get('dosagePerDay')?.value;
    const duration = this.prescriptionForm.get('durationDays')?.value;
    if (dosage && duration) {
        this.prescriptionForm.get('totalAmount')?.setValue(dosage * duration);
    }
  }

  get customerAge(): number | null {
    const birthDateStr = this.activeCustomer()?.birthDate;
    if (!birthDateStr) return null;
    const birthDate = new Date(birthDateStr);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  async generateWithAi() {
    if (!this.aiPrompt().trim() || !this.geminiService.isConfigured()) return;
    this.isGenerating.set(true);
    this.errorMessage.set(null);
    try {
        const detailsFromAi = await this.geminiService.generatePrescriptionDetails(this.aiPrompt());
        this.prescriptionForm.patchValue(detailsFromAi);
    } catch (e: any) {
        this.errorMessage.set(e?.message || 'Failed to parse prescription.');
    } finally {
        this.isGenerating.set(false);
    }
  }

  async submitReview(status: 'APPROVED' | 'REJECTED') {
    if (!this.isDoctor()) return;

    if (this.mode() === 'review' && status === 'REJECTED') {
      this.view.set('rejecting');
      return;
    }

    const currentPrescription = this.prescription();
    const currentCustomer = this.customer();
    const currentUser = this.authService.currentUser();
    
    // --- Soft Block: Check Signature ---
    if (status === 'APPROVED') {
        if (!currentUser?.signatureUrl) {
            this.notificationService.showError("ไม่พบข้อมูลลายเซ็นดิจิทัล กรุณาไปที่เมนู 'พนักงาน' เพื่อบันทึกลายเซ็นก่อนอนุมัติ");
            return;
        }
    }
    // -----------------------------------

    if (this.prescriptionForm.invalid) return;
    if (!currentUser?.uid) {
        this.errorMessage.set('Cannot save, doctor information is missing.');
        return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    try {
        const formValue = this.prescriptionForm.getRawValue();
        const details: PrescriptionDetail = formValue;
        let savedPrescriptionId = '';

        if (this.mode() === 'review' && currentPrescription) {
            await this.dataService.reviewPrescription(currentPrescription.id, 'APPROVED', null, details);
            savedPrescriptionId = currentPrescription.id;
        } else if (this.mode() === 'create' && currentCustomer) {
            const newPrescription = await this.dataService.createApprovedPrescription(currentCustomer as Customer, details, currentUser.uid);
            savedPrescriptionId = newPrescription.id;
        } else {
            throw new Error("Invalid mode or missing data for saving prescription.");
        }
        
        this.successPrescriptionId.set(savedPrescriptionId);
        this.view.set('success');
        this.prescriptionReviewed.emit();

    } catch (e: any) {
        this.errorMessage.set(e?.message || 'An unknown error occurred.');
    } finally {
        this.isSubmitting.set(false);
    }
  }

  async confirmRejection() {
    const currentPrescription = this.prescription();
    if (!currentPrescription || !this.isDoctor()) return;

    this.isSubmitting.set(true);
    this.errorMessage.set(null);
    try {
      const notes = this.rejectionNotes().trim() || null;
      await this.dataService.reviewPrescription(currentPrescription.id, 'REJECTED', notes, null);
      this.prescriptionReviewed.emit();
      this.onClose();
    } catch (e: any) {
      this.errorMessage.set(e?.message || 'An unknown error occurred.');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  cancelRejection() {
    this.view.set('main');
    this.rejectionNotes.set('');
    this.errorMessage.set(null);
  }

  printPrescription(): void {
    const id = this.successPrescriptionId();
    if (!id) return;
    
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/print/prescription', id])
    );
    this.document.defaultView?.open(url, '_blank');
  }

  onClose(): void {
    this.view.set('main');
    this.rejectionNotes.set('');
    this.errorMessage.set(null);
    this.successPrescriptionId.set(null);
    this.closeModal.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
